import { useForm } from '../../contexts/FormContext'
import { Button, SaveButton } from '../../components/common'
import { Input, Textarea } from '../../components/forms'
import './Step5Addons.css'

function Step5Addons() {
  const { formData, updateFormData, updateNestedFormData, goToNextStep, goToPreviousStep } = useForm()

  const handleDomainOptionChange = (option) => {
    updateNestedFormData('addons.domain.option', option)
  }

  const handleAddonToggle = (addon, checked) => {
    const addonCosts = {
      managedSupport: { setup: 0, monthlyPerUser: 25000 },
      corporateEmail: { setup: 10000, monthlyPerUser: 2000 },
      websiteChat: { setup: 10000, monthlyPerUser: 3900 }, // ¥3,900/user
      appointmentScheduler: { setup: 10000, monthlyPerUser: 1600 }, // ¥1,600/user
      businessCards: { setup: 35000, monthlyPerUser: 0 }
    }

    const cost = addonCosts[addon]
    const users = formData.addons[addon]?.users || 1
    const setupChange = checked ? cost.setup : -cost.setup
    const monthlyChange = checked ? cost.monthlyPerUser * users : -cost.monthlyPerUser * users

    updateNestedFormData(`addons.${addon}.selected`, checked)
    updateFormData({
      costs: {
        ...formData.costs,
        setupTotal: formData.costs.setupTotal + setupChange,
        monthlyRecurring: formData.costs.monthlyRecurring + monthlyChange
      }
    })
  }

  const canProceed = formData.addons.domain.option !== ''

  return (
    <div className="step-section">
      {/* Section Header */}
      <div className="section-header">
        <h2>Step 5: Enhance Your Website</h2>
        <p className="section-subtitle">Add professional services and tools to maximize effectiveness</p>
      </div>

      {/* Section Body */}
      <div className="section-body">
        {/* Domain Section */}
        <div className="addon-section">
          <div className="addon-section-header">
            <div className="addon-section-icon">🌐</div>
            <h3 className="addon-section-title">Domain Setup</h3>
          </div>
          <p className="addon-section-desc">Your domain name is your address on the internet. We'll help you get set up at no extra cost.</p>

          <div className="addon-card-single">
            <div className="price-badge-single free">FREE - Included</div>

            <div className="radio-options-styled">
              <label className={`radio-option-card ${formData.addons.domain.option === 'register_new' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="domainOption"
                  value="register_new"
                  checked={formData.addons.domain.option === 'register_new'}
                  onChange={(e) => handleDomainOptionChange(e.target.value)}
                />
                <div className="radio-indicator"></div>
                <div className="radio-label">Register a new domain for me</div>
              </label>

              {formData.addons.domain.option === 'register_new' && (
                <div className="expanded-content">
                  <div className="domain-input-group">
                    <Input
                      id="domain-name"
                      label="Preferred domain name"
                      placeholder="mycompany"
                      value={formData.addons.domain.newDomainName}
                      onChange={(e) => updateNestedFormData('addons.domain.newDomainName', e.target.value)}
                    />
                    <div className="form-group">
                      <label className="form-label">Extension</label>
                      <select
                        className="select"
                        value={formData.addons.domain.newDomainExtension}
                        onChange={(e) => updateNestedFormData('addons.domain.newDomainExtension', e.target.value)}
                      >
                        <option value=".jp">.jp</option>
                        <option value=".com">.com</option>
                        <option value=".co.jp">.co.jp</option>
                        <option value=".net">.net</option>
                      </select>
                    </div>
                  </div>
                  <div className="info-box">
                    <p>💡 Domain availability will be checked during setup. We'll contact you if your preferred domain is not available.</p>
                  </div>
                </div>
              )}

              <label className={`radio-option-card ${formData.addons.domain.option === 'existing' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="domainOption"
                  value="existing"
                  checked={formData.addons.domain.option === 'existing'}
                  onChange={(e) => handleDomainOptionChange(e.target.value)}
                />
                <div className="radio-indicator"></div>
                <div className="radio-label">I have an existing domain</div>
              </label>

              {formData.addons.domain.option === 'existing' && (
                <div className="expanded-content">
                  <Input
                    id="existing-domain"
                    label="Your existing domain"
                    placeholder="www.mycompany.jp"
                    value={formData.addons.domain.existingDomain}
                    onChange={(e) => updateNestedFormData('addons.domain.existingDomain', e.target.value)}
                  />
                  <div className="warning-box">
                    <p>⚠️ You'll need to update DNS settings to point to our servers. We'll provide detailed instructions.</p>
                  </div>
                </div>
              )}

              <label className={`radio-option-card ${formData.addons.domain.option === 'decide_later' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="domainOption"
                  value="decide_later"
                  checked={formData.addons.domain.option === 'decide_later'}
                  onChange={(e) => handleDomainOptionChange(e.target.value)}
                />
                <div className="radio-indicator"></div>
                <div className="radio-label">I'll decide on domain later</div>
              </label>
            </div>
          </div>
        </div>

        {/* Managed Support Section */}
        <div className="addon-section">
          <div className="addon-section-header">
            <div className="addon-section-icon">🛡️</div>
            <h3 className="addon-section-title">Ongoing Support</h3>
          </div>
          <p className="addon-section-desc">Keep your website secure, updated, and running smoothly with professional management.</p>

          <label
            className={`addon-card-checkable ${formData.addons.managedSupport.selected ? 'selected' : ''}`}
            onClick={() => handleAddonToggle('managedSupport', !formData.addons.managedSupport.selected)}
          >
            <div className="addon-card-header">
              <div className="addon-checkbox-custom"></div>
              <div className="addon-card-content">
                <h4 className="addon-card-title">Managed Support Services</h4>
                <div className="addon-card-pricing">
                  <span className="price-badge free">First 3 Months FREE</span>
                  <span className="price-badge monthly">¥25,000/month after</span>
                </div>
              </div>
            </div>
            <p className="addon-card-desc">Professional website management so you can focus on your business.</p>
            <ul className="addon-card-features">
              <li>Server hosting & technical management</li>
              <li>Regular software & security updates</li>
              <li>Performance optimization</li>
              <li>Email support</li>
              <li>Content updates (non-design)</li>
              <li>Domain renewal handling</li>
            </ul>

            {formData.addons.managedSupport.selected && (
              <div className="addon-expanded-section" onClick={(e) => e.stopPropagation()}>
                <div className="info-box">
                  <p>⚠️ <strong>Recommended:</strong> Without managed support, you'll need an IT engineer or system administrator for ongoing maintenance, updates, and security.</p>
                  <p style={{ marginTop: '0.5rem' }}>💡 Paid support automatically starts after your 3 free months.</p>
                </div>
              </div>
            )}
          </label>
        </div>

        {/* Communication Tools Section */}
        <div className="addon-section">
          <div className="addon-section-header">
            <div className="addon-section-icon">💬</div>
            <h3 className="addon-section-title">Communication Tools</h3>
          </div>
          <p className="addon-section-desc">Enhance customer engagement with professional communication solutions.</p>

          <div className="addon-cards-grid">
            {/* Corporate Email */}
            <label
              className={`addon-card-checkable ${formData.addons.corporateEmail.selected ? 'selected' : ''}`}
              onClick={() => handleAddonToggle('corporateEmail', !formData.addons.corporateEmail.selected)}
            >
              <div className="addon-card-header">
                <div className="addon-checkbox-custom"></div>
                <div className="addon-card-content">
                  <h4 className="addon-card-title">Corporate Email with Gmail</h4>
                  <div className="addon-card-pricing">
                    <span className="price-badge setup">Setup: ¥10,000</span>
                    <span className="price-badge monthly">~¥2,000/month per user</span>
                  </div>
                </div>
              </div>
              <p className="addon-card-desc">Professional email addresses using your domain powered by Google Workspace.</p>
              <ul className="addon-card-features">
                <li>Custom domain email addresses</li>
                <li>Gmail interface & apps</li>
                <li>30GB storage per user</li>
                <li>Calendar & Drive integration</li>
              </ul>

              {formData.addons.corporateEmail.selected && (
                <div className="addon-expanded-section" onClick={(e) => e.stopPropagation()}>
                  <Input
                    id="email-users"
                    label="Number of users"
                    type="number"
                    min="1"
                    max="50"
                    value={formData.addons.corporateEmail.users}
                    onChange={(e) => {
                      const users = parseInt(e.target.value) || 1
                      updateNestedFormData('addons.corporateEmail.users', users)
                      updateFormData({
                        costs: {
                          ...formData.costs,
                          monthlyRecurring: formData.costs.monthlyRecurring - (formData.addons.corporateEmail.users * 2000) + (users * 2000)
                        }
                      })
                    }}
                    helperText={`Monthly cost: ¥${(formData.addons.corporateEmail.users * 2000).toLocaleString()} (¥2,000 × ${formData.addons.corporateEmail.users} users)`}
                  />
                  <div className="info-box">
                    <p>Timeline: +1 week for setup and configuration</p>
                  </div>
                </div>
              )}
            </label>

            {/* Website Chat */}
            <label
              className={`addon-card-checkable ${formData.addons.websiteChat.selected ? 'selected' : ''}`}
              onClick={() => handleAddonToggle('websiteChat', !formData.addons.websiteChat.selected)}
            >
              <div className="addon-card-header">
                <div className="addon-checkbox-custom"></div>
                <div className="addon-card-content">
                  <h4 className="addon-card-title">Website Chat Widget</h4>
                  <div className="addon-card-pricing">
                    <span className="price-badge setup">Setup: ¥10,000</span>
                    <span className="price-badge monthly">¥3,900/month per user</span>
                  </div>
                </div>
              </div>
              <p className="addon-card-desc">Live chat with visitors directly from your website using Continually.</p>
              <ul className="addon-card-features">
                <li>Real-time visitor chat</li>
                <li>Mobile app notifications</li>
                <li>Chat history & analytics</li>
                <li>Automated greetings</li>
              </ul>

              {formData.addons.websiteChat.selected && (
                <div className="addon-expanded-section" onClick={(e) => e.stopPropagation()}>
                  <Input
                    id="chat-users"
                    label="Number of users"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.addons.websiteChat.users}
                    onChange={(e) => {
                      const users = parseInt(e.target.value) || 1
                      const oldUsers = formData.addons.websiteChat.users
                      updateNestedFormData('addons.websiteChat.users', users)
                      // Update monthly cost
                      updateFormData({
                        costs: {
                          ...formData.costs,
                          monthlyRecurring: formData.costs.monthlyRecurring - (oldUsers * 3900) + (users * 3900)
                        }
                      })
                    }}
                    helperText={`Monthly cost: ¥${(formData.addons.websiteChat.users * 3900).toLocaleString()} (¥3,900 × ${formData.addons.websiteChat.users} users)`}
                  />
                  <div className="info-box">
                    <p>Timeline: +3 days for setup and configuration</p>
                  </div>
                </div>
              )}
            </label>

            {/* Appointment Scheduler */}
            <label
              className={`addon-card-checkable ${formData.addons.appointmentScheduler.selected ? 'selected' : ''}`}
              onClick={() => handleAddonToggle('appointmentScheduler', !formData.addons.appointmentScheduler.selected)}
            >
              <div className="addon-card-header">
                <div className="addon-checkbox-custom"></div>
                <div className="addon-card-content">
                  <h4 className="addon-card-title">Appointment Scheduler</h4>
                  <div className="addon-card-pricing">
                    <span className="price-badge setup">Setup: ¥10,000</span>
                    <span className="price-badge monthly">¥1,600/month per user</span>
                  </div>
                </div>
              </div>
              <p className="addon-card-desc">Let clients book appointments online with Calendly integration.</p>
              <ul className="addon-card-features">
                <li>Online booking widget</li>
                <li>Calendar sync (Google/Outlook)</li>
                <li>Automated reminders</li>
                <li>Custom availability rules</li>
              </ul>

              {formData.addons.appointmentScheduler.selected && (
                <div className="addon-expanded-section" onClick={(e) => e.stopPropagation()}>
                  <Input
                    id="scheduler-users"
                    label="Number of users"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.addons.appointmentScheduler.users}
                    onChange={(e) => {
                      const users = parseInt(e.target.value) || 1
                      const oldUsers = formData.addons.appointmentScheduler.users
                      updateNestedFormData('addons.appointmentScheduler.users', users)
                      // Update monthly cost
                      updateFormData({
                        costs: {
                          ...formData.costs,
                          monthlyRecurring: formData.costs.monthlyRecurring - (oldUsers * 1600) + (users * 1600)
                        }
                      })
                    }}
                    helperText={`Monthly cost: ¥${(formData.addons.appointmentScheduler.users * 1600).toLocaleString()} (¥1,600 × ${formData.addons.appointmentScheduler.users} users)`}
                  />
                  <div className="info-box">
                    <p>Timeline: +3 days for setup and configuration</p>
                  </div>
                </div>
              )}
            </label>
          </div>
        </div>

        {/* Business Essentials Section */}
        <div className="addon-section">
          <div className="addon-section-header">
            <div className="addon-section-icon">💼</div>
            <h3 className="addon-section-title">Business Essentials</h3>
          </div>
          <p className="addon-section-desc">Complete your professional identity with printed materials.</p>

          <label
            className={`addon-card-checkable ${formData.addons.businessCards.selected ? 'selected' : ''}`}
            onClick={() => handleAddonToggle('businessCards', !formData.addons.businessCards.selected)}
          >
            <div className="addon-card-header">
              <div className="addon-checkbox-custom"></div>
              <div className="addon-card-content">
                <h4 className="addon-card-title">Business Card Package</h4>
                <div className="addon-card-pricing">
                  <span className="price-badge setup">¥35,000</span>
                </div>
              </div>
            </div>
            <p className="addon-card-desc">Professional business card design and printing. <strong>We'll match the design to your website</strong> (colors, logo, and overall aesthetic).</p>
            <ul className="addon-card-features">
              <li>Custom design matching your website</li>
              <li>500 printed cards included</li>
              <li>Delivered to your address</li>
              <li>FREE logo integration (if ordering logo design)</li>
              <li>Additional printing: ¥2,000 per 100 cards</li>
            </ul>

            {formData.addons.businessCards.selected && (
              <div className="addon-expanded-section" onClick={(e) => e.stopPropagation()}>
                <Textarea
                  id="delivery-address"
                  label="Delivery address"
                  required
                  value={formData.addons.businessCards.deliveryAddress}
                  onChange={(e) => updateNestedFormData('addons.businessCards.deliveryAddress', e.target.value)}
                  placeholder="Enter full delivery address including postal code..."
                  rows={3}
                />
                <div className="info-box">
                  <p>Timeline: +1 week for design and production (runs in parallel with website development)</p>
                </div>
              </div>
            )}
          </label>
        </div>
      </div>

      {/* Section Actions */}
      <div className="section-actions">
        <Button variant="secondary" size="md" onClick={goToPreviousStep}>
          ← Back
        </Button>
        <div className="actions-right">
          <SaveButton />
          <Button variant="primary" size="md" disabled={!canProceed} onClick={goToNextStep}>
            Next Step
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Step5Addons
